import {
  Modal as ChakraModal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Flex,
  Text,
  Box,
  Spinner,
  FormLabel,
  Input,
  Textarea,
  Select,
  useToast
} from '@chakra-ui/react'
import { useEffect, useState } from 'react'

import { formatDateTime } from '../../../helpers'
import { IOurStudent, IOurStudentFormFields } from '../../../interfaces/Testimonials/ITestimonials'
import { TestimonialsService } from '../../../services/Testimonials/TestimonialsService'

interface IProps {
  isOpen: boolean
  onClose: () => void
  onUpdated?: () => void
  id: number
  knowledgeAreas: { id: number; name: string }[]
}

const service = new TestimonialsService()

const baseFormErrors = {
  name: '',
  knowledge_area_id: '',
  description: '',
  video_url: '',
  image: ''
}

export function TestimonialsEditModal({ isOpen, onClose, onUpdated, id, knowledgeAreas }: IProps) {
  const [student, setStudent] = useState<IOurStudent | null>(null)
  const [form, setForm] = useState<IOurStudentFormFields>({
    name: '',
    knowledge_area_id: 0,
    description: '',
    video_url: '',
    image: undefined
  })
  const [formErrors, setFormErrors] = useState(baseFormErrors)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const toast = useToast()

  useEffect(() => {
    if (!id || !isOpen) return

    setLoading(true)
    setError(null)

    const fetchData = async () => {
      try {
        const data = await service.get(id)
        setStudent(data)

        setForm({
          name: data.name || '',
          knowledge_area_id: data.knowledge_area_id || 0,
          description: data.description || '',
          video_url: data.video_url || '',
          image: undefined
        })
      } catch (err) {
        setError('Erro ao carregar os dados do estudante.')
        setStudent(null)
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [id, isOpen])

  const handleSave = async () => {
    setSaving(true)
    setFormErrors(baseFormErrors)

    try {
      await service.update(id, form)
      toast({
        title: 'Estudante atualizado com sucesso',
        status: 'success',
        duration: 5000,
        isClosable: true
      })
      onClose()
      onUpdated?.()
    } catch (error: any) {
      if (error.response?.status === 422) {
        setFormErrors(error.response.data.errors)
      } else {
        toast({
          title: 'Erro ao atualizar estudante',
          status: 'error',
          duration: 3000,
          isClosable: true
        })
      }
    } finally {
      setSaving(false)
    }
  }

  return (
    <ChakraModal isOpen={isOpen} onClose={onClose} size="3xl" isCentered scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Editar Estudante</ModalHeader>
        <ModalBody>
          {loading && (
            <Flex justify="center" py={10}>
              <Spinner />
            </Flex>
          )}

          {!loading && error && (
            <Text color="red.500" textAlign="center" mb={4}>
              {error}
            </Text>
          )}

          {!loading && student && (
            <Flex direction="column" gap={4}>
              <Box>
                <FormLabel htmlFor="name">Nome</FormLabel>
                <Input
                  id="name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  disabled={saving}
                />
                {formErrors.name && <Text color="red.500">{formErrors.name}</Text>}
              </Box>

              <Box>
                <FormLabel htmlFor="knowledge_area_id">Área de Conhecimento</FormLabel>
                <Select
                  id="knowledge_area_id"
                  value={form.knowledge_area_id}
                  onChange={(e) => setForm({ ...form, knowledge_area_id: parseInt(e.target.value) })}
                  disabled={saving}
                >
                  <option value={0}>Selecione uma área</option>
                  {knowledgeAreas.map((area) => (
                    <option key={area.id} value={area.id}>
                      {area.name}
                    </option>
                  ))}
                </Select>
                {formErrors.knowledge_area_id && <Text color="red.500">{formErrors.knowledge_area_id}</Text>}
              </Box>

              <Box>
                <FormLabel htmlFor="description">Depoimento</FormLabel>
                <Textarea
                  id="description"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  disabled={saving}
                />
                {formErrors.description && <Text color="red.500">{formErrors.description}</Text>}
              </Box>

              <Box>
                <FormLabel htmlFor="video_url">URL do Vídeo</FormLabel>
                <Input
                  id="video_url"
                  value={form.video_url}
                  onChange={(e) => setForm({ ...form, video_url: e.target.value })}
                  disabled={saving}
                />
                {formErrors.video_url && <Text color="red.500">{formErrors.video_url}</Text>}
              </Box>

              <Box>
                <FormLabel htmlFor="image">Imagem (novo upload para substituir)</FormLabel>
                <Input
                  id="image"
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0]
                    if (file) setForm({ ...form, image: file })
                  }}
                  disabled={saving}
                />
                {formErrors.image && <Text color="red.500">{formErrors.image}</Text>}
              </Box>

              <Text fontSize="sm" color="gray.500" textAlign="right">
                Criado em: {student.created_at ? formatDateTime(student.created_at) : 'Não informado'}
              </Text>
            </Flex>
          )}
        </ModalBody>
        <ModalFooter>
          <Button colorScheme="magenta" mr={3} onClick={handleSave} disabled={saving || loading}>
            {saving ? 'Salvando...' : 'Salvar'}
          </Button>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
        </ModalFooter>
      </ModalContent>
    </ChakraModal>
  )
}
